import React, { useState, useEffect } from 'react'
import { Grid, TextField, FormControl, InputLabel, Select, MenuItem } from '@material-ui/core'
import Checkbox from '@material-ui/core/Checkbox'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import Button from '@material-ui/core/Button'
import DeleteIcon from '@material-ui/icons/Delete'

import { Dialog, snackbar } from 'components'
import { useApiCall } from 'hooks'
import { Tag } from 'features/Main/Tags/types'

import { UserPoll, UpdateUserPollDetailResponse, UserPollDetail } from '../../types'
import {
  getUserPollDetails,
  updateUserPollDetail,
  createUserPollDetail,
  deleteUserPollDetail,
} from '../../api'
import { useStyles } from './styles'
import { constants } from '../../constansts'

interface CreateUserPollDialogProps {
  handleClose: () => void
  userPoll: UserPoll
  tags: Tag[]
}

const MAX_ACTIVE_ANSWERS = 6

const UserPollDetailDialog: React.FC<CreateUserPollDialogProps> = ({
  handleClose,
  userPoll,
  tags,
}) => {
  const classes = useStyles()
  const [userPollDetails, setUserPollDetails] = useState(userPoll.detail)
  const [currentUserPollDetail, setCurrentUserPollDetail] = useState<UserPollDetail[]>([])
  const [inputValue] = useState<string | File | boolean | null>('')
  const [icon] = useState<File | null>(null)
  const [inputField, setInputField] = useState('')
  const [updateUserPollDetailApi] = useApiCall<
    {
      id: number
      userPollId: number
      active?: boolean
      answer?: string
      tag?: string
      icon?: File
    },
    UpdateUserPollDetailResponse
  >(updateUserPollDetail)
  const [createUserPollDetailApi] = useApiCall<
    {
      id: number
      userPollId: number
      active?: boolean
      answer?: string
      tag?: string
      icon?: File
    },
    UpdateUserPollDetailResponse
  >(createUserPollDetail)
  const [getUserPollDetailsApi, isLoading] = useApiCall<UserPoll, UserPollDetail[]>(
    getUserPollDetails,
  )
  const [deleteUserPollApi] = useApiCall<UserPollDetail, void>(deleteUserPollDetail)

  useEffect(() => {
    if (userPoll) {
      const fetchData = async () => {
        const userPollDetailsData = await getUserPollDetailsApi(userPoll)

        if (userPollDetailsData) setUserPollDetails(userPollDetailsData)
      }
      fetchData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userPoll])

  useEffect(() => {
    if (!currentUserPollDetail.length && inputField !== 'answer') {
      snackbar.show(`Debe asignar un titulo primero`)
      return
    }

    const timer = setTimeout(() => {
      const updateOrCreateUserPollDetail = async () => {
        if (icon && typeof icon.name === 'string') {
          const updatedDetails = currentUserPollDetail.map(detail =>
            detail.id ? { ...detail, icon } : detail,
          )
          setCurrentUserPollDetail(updatedDetails)
        }
        if (inputField === 'tag' && typeof inputValue === 'number') {
          const updatedDetails = currentUserPollDetail.map(detail =>
            detail.id ? { ...detail, tag: inputValue } : detail,
          )
          setCurrentUserPollDetail(updatedDetails)
        }

        const newUserPollDetails = await Promise.all(
          currentUserPollDetail.map(async detail => {
            if (detail.id) {
              const response = await updateUserPollDetailApi({
                id: detail.id,
                userPollId: detail.userPollId || userPoll.id,
                active: detail.active || undefined,
                answer: detail.answer,
                tag: detail.tag?.toString(),
                icon: detail.icon,
              })
              return response.userPollDetail
            }
            const response = await createUserPollDetailApi({
              id: 0, // Temporary ID for creation
              userPollId: userPoll.id,
              active: detail.active || undefined,
              answer: detail.answer,
              tag: detail.tag?.toString(),
              icon: detail.icon,
            })
            return response.userPollDetail
          }),
        )

        if (!currentUserPollDetail.length && newUserPollDetails.length && inputField === 'answer') {
          setUserPollDetails(newUserPollDetails)
        } else {
          setUserPollDetails(newUserPollDetails)
        }

        snackbar.show(`Respuesta actualizada`)
      }
      updateOrCreateUserPollDetail()
    }, 500)

    // eslint-disable-next-line consistent-return
    return (): void => {
      clearTimeout(timer)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputValue, currentUserPollDetail, inputField])

  const handleTitleChange = async (
    // eslint-disable-next-line @typescript-eslint/member-delimiter-style
    e: React.ChangeEvent<{ name?: string; value: unknown }>,
    idx: number,
    item: UserPollDetail,
  ) => {
    const newUserPollDetails = [...userPollDetails]
    newUserPollDetails[idx] = { ...item, answer: e.target.value as string }
    setUserPollDetails(newUserPollDetails)
  }

  const handleTagChange = async (
    // eslint-disable-next-line @typescript-eslint/member-delimiter-style
    e: React.ChangeEvent<{ name?: string; value: unknown }>,
    item: UserPollDetail,
  ) => {
    const newUserPollDetails = [...userPollDetails]
    const index = newUserPollDetails.findIndex(el => el.id === item.id)
    const tagValue = parseInt(e.target.value as string, 10)
    if (Number.isNaN(tagValue)) {
      snackbar.show(`El tag debe ser un número`)
      return
    }
    newUserPollDetails[index] = { ...item, tag: tagValue }
    setUserPollDetails(newUserPollDetails)
  }

  const handleActiveChange = async (item: UserPollDetail) => {
    const activeCount = currentUserPollDetail.filter(detail => detail.active).length
    if (!item.active && activeCount >= MAX_ACTIVE_ANSWERS) {
      snackbar.show(
        `Sólo está permitido tener un máximo de ${MAX_ACTIVE_ANSWERS} respuestas activas`,
      )
      return
    }

    if (!item.tag) {
      snackbar.show(`Debe seleccinar un tag para activar esta respuesta`)
      return
    }

    const updatedDetails = currentUserPollDetail.map(detail => {
      if (detail.id === item.id) {
        return { ...detail, active: !detail.active }
      }
      return detail
    })

    setCurrentUserPollDetail(updatedDetails)
    setInputField('active')
    setUserPollDetails(updatedDetails)
  }

  const handleAddNewItem = () => {
    const existsEmpty = userPollDetails.find(el => el.answer === '')
    if (existsEmpty) {
      snackbar.show(`Tiene un elemento sin completar aún`)
      return
    }

    const newItem = {
      id: null,
      answer: '',
      iconUrl: '',
      active: null,
      tag: null,
      userPollId: userPoll.id,
    }
    setUserPollDetails([...userPollDetails, newItem])
  }

  const handleRemoveItem = async (item: UserPollDetail) => {
    const userPollDetailsToDelete = userPollDetails.find(el => item.id === el.id)
    if (!userPollDetailsToDelete) return

    const newUserPollDetails = userPollDetails.filter(el => el !== item)
    setUserPollDetails(newUserPollDetails)

    await deleteUserPollApi(userPollDetailsToDelete)
    snackbar.show(`Respuesta eliminada`)
  }

  const handleIsChecked = (active: boolean | null | undefined) => {
    if (!active) return false
    return true
  }

  const isChangeTagDisabled = (item: UserPollDetail) => {
    if (!item.tag) return false

    return item.active !== null
  }

  return (
    <Dialog
      title={`${constants.TEST_NAME} respuestas`}
      isOpen
      showActions
      onCancel={handleClose}
      isLoading={isLoading}
      readOnly
      contentStyle={{ minWidth: 800 }}
      style={{ minWidth: '80%' }}
    >
      <Grid container spacing={3}>
        {userPollDetails &&
          userPollDetails.map((el, index) => (
            <>
              <Grid item lg={7} xs={7}>
                <TextField
                  label="Respuesta"
                  value={el.answer}
                  fullWidth
                  onChange={ev => handleTitleChange(ev, index, el)}
                  disabled={isLoading}
                  id={`${el.id}`}
                  inputProps={{ maxLength: 30 }}
                />
              </Grid>
              <Grid item xs={3} sm={3}>
                <InputLabel className={classes.multiselectTitle}>Tag</InputLabel>
                <FormControl className={classes.fullWidth}>
                  <Select
                    labelId={`demo-simple-select-label-${el.id}`}
                    id={`demo-simple-select-${el.id}`}
                    value={el.tag}
                    // eslint-disable-next-line @typescript-eslint/member-delimiter-style
                    onChange={(e: React.ChangeEvent<{ name?: string; value: unknown }>) =>
                      handleTagChange(e, el)
                    }
                    disabled={isChangeTagDisabled(el)}
                  >
                    {tags.map(tag => (
                      <MenuItem value={tag.id}>
                        {tag.id} - {tag.title}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={1} sm={1}>
                <FormControlLabel
                  control={
                    <Checkbox
                      onChange={() => handleActiveChange(el)}
                      checked={handleIsChecked(el.active)}
                    />
                  }
                  label="Activar"
                />
              </Grid>
              <Grid item xs={1} sm={1}>
                <Button onClick={() => handleRemoveItem(el)}>
                  <DeleteIcon />
                </Button>
              </Grid>
            </>
          ))}
        <Grid item xs={12} sm={12}>
          <Button onClick={handleAddNewItem} variant="contained" color="secondary" autoFocus>
            Agregar respuesta
          </Button>
        </Grid>
      </Grid>
    </Dialog>
  )
}

export { UserPollDetailDialog }
